import { DisplayStatusCodes, OutvioCode } from './IPortalSettings';

export enum Emojis {
  SAD = '😞',
  MKAY = '😕',
  HAPPY = '🙂',
  BIG_SMILE = '😁',
  STARRY_EYES = '🤩',
}

export type StatusStepsDisplay = 'full-steps' | 'incidents' | 'categories';

export enum StepStatus {
  PENDING = 'pending',
  DONE = 'done',
  INCIDENT = 'incident',
  BACKTRACK_INCIDENT = 'backtrack_incident',
  BACKTRACK = 'backtrack',
  PAST_INCIDENT = 'past_incident',
}

export interface IOrderStatusInfoData {
  currentStep: DisplayStep;
  isIncident: boolean;
  incidentUrl?: string;
  steps: DisplayStep[];
}

export interface IDeliveryContact {
  fullName: string;
  email?: string;
  phone?: string;
}

export interface IDeliveryAddress {
  street: string;
  city: string;
  country: string;
  postcode: string;
}

export interface IDeliveryInfoData {
  contact: IDeliveryContact;
  address: IDeliveryAddress;
  comment?: string;
  courier: string;
  canUserEdit: boolean; // Is edit blocked because of time
}

export interface IOrderProduct {
  name: string;
  amount: number;
  price: string;
  imgSrc?: string;
  variant?: string;
  discountPrice?: string | null;
}

export interface IOrderInfoData {
  shippingPrice: string;
  vat: string;
  orderTotal: string;
  products: IOrderProduct[];
  otn: string;
  trackingNumber?: string;
  secondaryTrackingNumber?: string;
  originalId?: string;
  id?: string;
  canUserCancel: boolean;
  refund: string; // Value for refunding
  method?: string;
}

export interface ICoordinates {
  lng: number;
  lat: number;
}

export interface IMarker extends ICoordinates {
  isShipmentHere: boolean;
}

export interface IMap {
  markers: Promise<IMarker[]>;
}

export interface ITrackingData {
  orderStatusInfo: IOrderStatusInfoData;
  deliveryInfo: IDeliveryInfoData;
  orderInfo: IOrderInfoData;
  map: IMap;
  otn: string;
  trackingLink: Promise<string | null>;
  rating?: number;
  timeZone?: string;
  cmsInfo?: {
    // Deprecated
    zoneOffset?: string; // Format +00:00
  };
  internalStatus?: string;
  shouldDisplayMap?: boolean;
  estimatedDeliverDate?: string;
  recommendedProducts?: {
    title: string;
    imageUrl: string;
    originalPrice: number;
    discountedPrice?: number;
    currency: string;
    link: string;
  }[];
}

export interface SourceSteps {
  steps: SourceStep[];
}

export interface SourceStep {
  date: string;
  location?: string | null;
  code: OutvioCode;
  description?: string;
  courier: string;
  courierDescription?: string | null;
  courierCode?: string;
  eventCode?: string | null;
  status?: string;
  substatus?: string;
  coordinates?: ICoordinates;
  serviceAreaCode?: unknown;
  pickupPoint?: string;
  packageId?: string;
}

export interface OrderStep {
  status: StepStatus;
  displayCode: DisplayStatusCodes;
  code: OutvioCode;
  description?: string;
  location?: string;
  dateTime: string;
  coordinates?: ICoordinates;
}

export interface CategorizedStep {
  displayCode: DisplayStatusCodes;
  steps: OrderStep[];
}

export interface DisplayStep extends OrderStep {
  isSubStep: boolean;
  isFirst: boolean;
  isLast: boolean;
  prevStatus?: StepStatus;
  nextStatus?: StepStatus;
  isLastDoneStep: boolean;
  parent?: {
    prevStatus?: StepStatus;
    nextStatus?: StepStatus;
    status: StepStatus;
  };
}

export enum Currency {
  EUR = 'EUR',
  PLN = 'PLN',
  GBP = 'GBP',
  USD = 'USD',
}

export const CURRENCY_SYMBOLS = {
  [Currency.EUR]: '€',
  [Currency.PLN]: 'zł.',
  [Currency.GBP]: '£',
  [Currency.USD]: '$',
};

/**
 * The OutvioCode that MUST belong as substeps of specific DisplayStatusCodes.
 * The order of the steps array matters.
 */
export const SPECIFIC_SUBSTEPS: { [key in OutvioCode]?: DisplayStatusCodes[] } = {
  [OutvioCode.BAD_ADDRESS]: [OutvioCode.IN_TRANSIT, OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.DAMAGED]: [OutvioCode.IN_TRANSIT, OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.DESTROYED]: [OutvioCode.IN_TRANSIT, OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.DELAYED]: [OutvioCode.IN_TRANSIT],
  [OutvioCode.MISSING_DOCUMENTATION]: [OutvioCode.IN_TRANSIT, OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.LOST]: [OutvioCode.IN_TRANSIT, OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.NOT_AT_HOME]: [OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.PARTIALLY_DELIVERED]: [OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.REFUSED]: [OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.RETURN_TO_ORIGIN]: [OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.STOLEN]: [OutvioCode.IN_TRANSIT, OutvioCode.OUT_FOR_DELIVERY],
  [OutvioCode.SENT_TO_AGENCY]: [OutvioCode.IN_TRANSIT],
};

interface SourceOriginAddress {
  name?: string;
  postcode: string;
  city: string;
  address: string;
  country: string;
  countryCode: string;
  contactName: string;
  contactPhone: string;
  coordinates: ICoordinates;
}

export interface SourceDestinationAddress {
  comment?: string;
  address: string;
  city: string;
  state?: string;
  country: string;
  countryCode: string;
  postcode: string;
  phone?: string;
  email?: string;
  name: string;
  coordinates: ICoordinates;
}

interface Product {
  amount: number;
  discountPrice?: number | null;
  imgSrc: string;
  name: string;
  price: number;
  variant: string;
}

export interface SourceTrackingData {
  trackingNumber?: string;
  secondaryTrackingNumber?: string;
  otn: string;
  originAddress: SourceOriginAddress;
  origin: string;
  destination: string;
  processDate: string;
  pickingDate?: string;
  estimatedDeliverDate?: string;
  support?: string;
  destinationAddress: SourceDestinationAddress;
  courier: string;
  method?: string;
  shippingPrice: number;
  total: number;
  vat: number;
  currency: Currency;
  products: Product[];
  originalId?: string;
  id?: string;
  rating?: { rating: number } | null;
  canEditDeliveryAddress: boolean;
  dateTime?: string;
  createdAt: string;
  isSplitOrder?: boolean;
  orderOtn: string;
  isDeleted?: boolean;
  internalStatus?: string;
  cancelingAllowed: boolean;
  cmsInfo?: {
    zoneOffset?: string; // Format +00:00
  };
  timeZone?: string;
  formattedTimes?: {
    processDate?: string;
    pickingDate?: string;
    createdAt?: string;
    dateTime?: string;
  };
  returnData?: {
    hasExchange: boolean;
    hasReturn: boolean;
    creditAmount: number;
    couponTrigger: any;
    clientPays: number;
    storeReturns: number;
    discountType: any;
    specialActionType: any;
    refundMethod: string;
  };
  shouldDisplayMap?: boolean;
  recommendedProducts?: {
    title: string;
    imageUrl: string;
    originalPrice: number;
    currency: string;
    link: string;
    discountedPrice?: number;
  }[];
}
